import { useTranslation } from 'react-i18next'
import { observe, useObserver, viewportWidth } from 'react-ui-observer'
import get from 'lodash/get'
import styled, { css } from 'styled-components'
import { mb, mt } from 'styled-components-spacing'

import { breakpoints } from 'bl-common/src/constants/breakpoints'
import { colors } from 'bl-common/src/constants/colors'
import { durations } from 'bl-common/src/constants/durations'
import { base } from 'bl-common/src/constants/sizes'
import { Link } from 'bl-common/src/elements/Link'
import { Type } from 'bl-common/src/elements/Typography/Typography'
import { getImageUrl } from 'bl-common/src/units/SmartImage/getImageUrl'
import { StyledImage } from 'bl-common/src/units/SmartImage/StyledImage'
import { between } from 'bl-common/src/utils/between'
import { media, mediaMax } from 'bl-common/src/utils/media'
import { formatDateInUTC } from 'bl-utils/src/formatting/formatDate'

import { ReadMore } from './ReadMore'

type ContainerProps = {
  top?: any
  bottom?: any
}

type ImageWrapProps = {
  verticalOffsetDirection?: string
}

type ImageProps = {
  ratio?: any
}

type MobileContainerProps = {
  image?: string
}

export const Container = styled.section<ContainerProps>`
  display: flex;
  flex: 1;
  position: relative;

  ${({ top, theme }) => mt(top, theme)};

  ${({ top }) =>
    top?.xs &&
    mediaMax.md(css`
      margin-top: ${top.xs * base}px;
    `)};

  ${({ bottom, theme }) => mb(bottom, theme)};

  ${({ bottom }) =>
    bottom?.xs &&
    mediaMax.md(css`
      margin-bottom: ${bottom.xs * base}px;
    `)};

  ${media.md(css`
    background: linear-gradient(149.34deg, #f9f9f9 0%, #e0d8d5 100%);
  `)}
`

export const Content = styled.div`
  padding: ${({ theme }) => `${theme.spacing[8]} ${theme.spacing[5]}`};
  padding-right: 0;
  margin-right: 40px;
  flex: 1;

  ${media.md(css`
    max-width: ${between(600, 800)};
    margin-right: ${({ theme }) => theme.spacing[6]};
  `)}
`

export const DateWrap = styled.div`
  display: flex;
  opacity: 0.7;
  &::before {
    content: '—';
    display: block;
    margin-right: 10px;
  }
`

export const Image = styled(StyledImage)<ImageProps>`
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  z-index: 1;
  transition:
    opacity ${durations.short}ms,
    transform ${durations.long}ms;

  &::before {
    content: '';
    display: block;
    padding-bottom: ${({ ratio }) => ratio * 100}%;
  }

  ${media.md(css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  `)}
`

export const ImageWrap = styled.div<ImageWrapProps>`
  position: relative;
  flex: 1;
  overflow: hidden;
  width: 100%;
  margin-left: auto;
  margin-right: ${({ theme }) => theme.spacing[5]};

  ${({ verticalOffsetDirection = 'top' }) =>
    verticalOffsetDirection === 'bottom'
      ? css`
          bottom: ${({ theme }) => theme.spacing[5]};
        `
      : css`
          top: ${({ theme }) => theme.spacing[5]};
        `}

  ${media.md(css`
    &:hover ${Image} {
      transform: scale(1.035);
      opacity: 1;
    }
  `)}
`

export const MobileContainer = styled.div<MobileContainerProps>`
  height: 100vh;
  width: 100%;
  color: white;
  display: flex;

  ${({ image }) =>
    image &&
    css`
      background-image: linear-gradient(
          to bottom,
          rgba(0, 0, 0, 0.2),
          rgba(0, 0, 0, 0.6)
        ),
        url(${image});
      background-size: cover;
    `}
`

export const MobileContent = styled.div`
  min-height: 40vh;
  margin: auto 36px 80px;
  width: 100%;
`

export const MobileHeadline = styled.div`
  padding-bottom: 32px;
  &::after {
    content: '';
    width: 32px;
    display: block;
    height: 1px;
    background: white;
  }
`

export const InnerLink = styled(({ ...rest }) => <Link {...rest} />)`
  position: relative;
  z-index: 2;
  display: block;

  &:hover {
    color: ${colors.softBlue};
  }
`

const RenderMobileVersion = ({ title, slug, topic, heroImage, introText }) => {
  const topicName = get(topic, 'fields.topicName', '')
  const image = get(heroImage, 'fields.image')
  const imageUrl = getImageUrl(image, { height: 1200 })

  return (
    <MobileContainer image={imageUrl}>
      <MobileContent>
        <MobileHeadline>
          <Type preset="headlineMedium">Featured story</Type>
        </MobileHeadline>
        <Type preset="labelSmall" weight="bold" bottom={{ xs: 1 }}>
          {topicName}
        </Type>
        <Type preset="headlineExtraLarge" bottom={{ xs: 0.5 }}>
          <Link to={`/stories/${slug}`}>{title}</Link>
        </Type>
        <Type preset="text" bottom={{ xs: 0.5 }}>
          {introText}
        </Type>
      </MobileContent>
    </MobileContainer>
  )
}

export const FeaturedStory = ({
  story: { fields = {} },
  top = {},
  bottom = {},
  verticalOffsetDirection = undefined,
}) => {
  const { i18n } = useTranslation()
  const { title, publishDate, slug, topic, heroImage, introText } =
    fields as any
  const date = formatDateInUTC(publishDate, 'PP', (i18n as any).locale)
  const topicName = get(topic, 'fields.topicName', '')
  const topicSlug = get(topic, 'fields.topicSlug', '')

  // TODO: Switch to useBreakpoints
  const isMobile = useObserver(
    observe(viewportWidth(), width => width < breakpoints.md)
  )

  return isMobile ? (
    <Container top={top} bottom={bottom}>
      <RenderMobileVersion
        title={title}
        topic={topic}
        heroImage={heroImage}
        introText={introText}
        slug={slug}
      />
    </Container>
  ) : (
    <Container top={top} bottom={bottom}>
      <Content>
        <Type
          preset="labelSmall"
          weight="bold"
          color={colors.deepBlue}
          bottom={{ xs: 1.5 }}
        >
          <InnerLink to={`/stories/topic/${topicSlug}`}>{topicName}</InnerLink>
        </Type>
        <Link to={`/stories/${slug}`}>
          <Type
            preset="headlineLarge"
            as="h2"
            weight="bold"
            bottom={{ xs: 1.5 }}
          >
            {title}
          </Type>
        </Link>
        <Type preset="subtitle" bottom={{ xs: 1 }}>
          {introText}
        </Type>
        <DateWrap>
          <Type preset="text" bottom={{ xs: 4, md: 3 }}>
            {date}
          </Type>
        </DateWrap>
        <Link to={`/stories/${slug}`}>
          <ReadMore />
        </Link>
      </Content>
      <ImageWrap verticalOffsetDirection={verticalOffsetDirection}>
        <Link to={`/stories/${slug}`}>
          <Image image={get(heroImage, 'fields.image')} ratio={1.3} />
        </Link>
      </ImageWrap>
    </Container>
  )
}
